import React, { createContext, useState, useMemo, useEffect } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useMediaQuery } from "@mui/material";
import { Fade } from "@mui/material";

// Function to generate theme dynamically
const getTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: "#1E3A8A",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#FFD700",
        contrastText: "#1E3A8A",
      },
      background: {
        default: mode === "dark" ? "#111827" : "#F9FAFB",
        paper: mode === "dark" ? "#1F2937" : "#FFFFFF",
      },
      text: {
        primary: mode === "dark" ? "#E5E7EB" : "#1E3A8A",
        secondary: mode === "dark" ? "#9CA3AF" : "#6B7280",
      },
    },
    typography: {
      fontFamily: "'Inter', sans-serif",
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            transition: "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            transition: "background-color 0.3s ease-in-out",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            transition: "all 0.3s ease-in-out",
          },
        },
      },
    },
  });

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const storedTheme = localStorage.getItem("theme");
  const defaultMode = storedTheme || (prefersDarkMode ? "dark" : "light");

  const [mode, setMode] = useState(defaultMode);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Memoized Theme
  const theme = useMemo(() => getTheme(mode), [mode]);

  // Toggle Theme with Animation
  const toggleTheme = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      const newMode = mode === "light" ? "dark" : "light";
      setMode(newMode);
      localStorage.setItem("theme", newMode);
      setIsTransitioning(false);
    }, 300);
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <Fade in={!isTransitioning} timeout={300}>
          <div>{children}</div>
        </Fade>
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
